<template>
    <!-- Mounted once, above everything.
      -- pointer-events: none is not decoration: a panel that took the pointer
      -- would land under the cursor, count as leaving the thumbnail, hide
      -- itself, put the cursor back over the thumbnail, and flicker for ever.
      -->
    <transition name="peek">
        <div v-if="peek.src" class="peek-layer" aria-hidden="true">
            <!-- The box is the space that is FREE; the picture fills as much of
              -- it as its own shape and its own resolution allow. Sizing the
              -- picture directly meant guessing its aspect ratio before it had
              -- loaded, and capping it at a number. -->
            <div class="peek-box" :style="box">
                <img :src="peek.src" alt="" class="peek-image" />
            </div>
        </div>
    </transition>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue"
import { peek, hide, reanchor } from "../compositions/thumb-peek"

// Clear of the thumbnail, so the preview never sits on the thing being
// pointed at.
const GAP = 14
const MARGIN = 12

const viewport = ref({w: 0, h: 0})

function measure() {
    viewport.value = {w: window.innerWidth, h: window.innerHeight}
}

onMounted(() => {
    measure()
    window.addEventListener("resize", measure)
    // A scroll RE-MEASURES rather than hides: see reanchor(). Hiding here
    // meant a lazy image arriving was enough to take the preview away.
    window.addEventListener("scroll", reanchor, {passive: true})
})

onBeforeUnmount(() => {
    window.removeEventListener("resize", measure)
    window.removeEventListener("scroll", reanchor)
})

/* The free space beside the thumbnail, all of it.
 *
 * This used to be a 520px panel placed to the right where it fitted. On a wide
 * screen that left most of the window empty and the picture smaller than it
 * needed to be -- the whole point is to look at the page properly, and a
 * newspaper front page at 520px is still a thumbnail.
 *
 * So: take whichever side of the thumbnail has more room, take ALL of it, and
 * let the picture fill what it can of that box. Two things fall out of doing it
 * this way rather than computing a size:
 *
 *   - the aspect ratio never has to be guessed. A portrait page ends up tall
 *     and narrow, a landscape one short and wide, and neither is distorted,
 *     because the box is the limit and max-width/max-height do the fitting.
 *   - a picture is never enlarged past its own resolution. The 360px variant
 *     in the versions table stays 360px wide instead of being blown up to
 *     1100px of blur, because max-width caps it and nothing stretches it.
 *
 * The thumbnail is never covered: the box starts a gap clear of it.
 */
const box = computed(() => {
    const a = peek.anchor
    const vw = viewport.value.w || 1024
    const vh = viewport.value.h || 768
    const height = Math.max(120, vh - MARGIN * 2)

    if (!a) {
        return {left: `${MARGIN}px`, top: `${MARGIN}px`,
                width: `${vw - MARGIN * 2}px`, height: `${height}px`}
    }

    const roomRight = vw - a.right - GAP - MARGIN
    const roomLeft = a.left - GAP - MARGIN
    const right = roomRight >= roomLeft
    const width = Math.max(120, right ? roomRight : roomLeft)
    const left = right ? a.right + GAP : Math.max(MARGIN, a.left - GAP - width)

    return {
        left: `${Math.round(left)}px`,
        top: `${MARGIN}px`,
        width: `${Math.round(width)}px`,
        height: `${Math.round(height)}px`,
        // Against the thumbnail's side of the box, not in the middle of it.
        // Centred, a portrait page in a 1200px-wide box sat 290px away from
        // the row it belonged to -- out in the middle of the screen, beside
        // nothing. The box is the space it MAY use; this is where it starts.
        justifyContent: right ? "flex-start" : "flex-end",
    }
})
</script>

<style scoped>
.peek-layer {
    position: fixed;
    inset: 0;
    z-index: 9000;      /* over the table, the sticky bars and the ad slots */
    pointer-events: none;
}

.peek-box {
    position: fixed;
    display: flex;
    align-items: center;   /* justify-content comes from the style binding */
}

.peek-image {
    /* The box is the limit and these do the fitting: the picture grows to fill
       whichever dimension binds first and is never enlarged past its own
       resolution, because neither of these can stretch it. */
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    border-radius: 12px;
    border: 1px solid var(--rule-strong);
    background: var(--stage);
    box-shadow: 0 18px 50px rgba(0, 0, 0, 0.45);
}

.peek-enter-active,
.peek-leave-active {
    transition: opacity 110ms ease-out;
}

.peek-enter-from,
.peek-leave-to {
    opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
    .peek-enter-active,
    .peek-leave-active {
        transition: none;
    }
}
</style>
